// frontend/src/pages/relatorios/RelatoriosPage.jsx
// ATUALIZADO: 07/11/2025
// Reflete a estrutura real do sistema após refatoração de atendimentos_especialistas
// e remoção de tipos_responsavel (deprecated 04/11/2025)
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
  LineChart, Line
} from "recharts";
import api from "../../services/api";
import echo from "../../services/echo";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import ProtectedPage from "../../components/common/ProtectedPage";

// Paleta com alto contraste para tema escuro
const COLORS = ["#F94144", "#577590", "#F8961E", "#43AA8B", "#90BE6D", "#8B5CF6", "#0EA5E9"];

const AREA_COLORS = {
  Promotoria:     COLORS[0], // vermelho
  Defensoria:     COLORS[1], // azul
  "Saúde":        COLORS[2], // laranja
  "Educação":     COLORS[3], // verde teal
  Especialistas:  COLORS[4], // verde claro
};

// Cores específicas para o gráfico "Status das movimentações"
const STATUS_COLORS = {
  "Finalizadas": "#577590", // azul
  "Em aberto":  "#F59E0B",  // amarelo
};

// Estilo padrão de tooltip (texto claro + fundo escuro)
const TOOLTIP = {
  contentStyle: {
    background: "#0b1511",
    border: "1px solid #1e3a2d",
    borderRadius: 8,
    color: "#e6f1ee",
    boxShadow: "0 2px 12px rgba(0,0,0,.45)",
  },
  labelStyle: { color: "#8fd3a8" },
  itemStyle: { color: "#e6f1ee" },
};

// -------- utils de data/tempo --------
function fmtDDMM(d) {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}`;
}
function diffMinutes(a, b) {
  if (!a || !b) return null;
  const ms = b.getTime() - a.getTime();
  return ms > 0 ? Math.round(ms / 60000) : null;
}

// -------- label custom para a pizza (percentual dentro) --------
const RADIAN = Math.PI / 180;
function renderPiePercentLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }) {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const pct = Math.round(percent * 100);
  if (pct === 0) return null; // evita poluir com 0%
  return (
    <text x={x} y={y} fill="#f1f5f9" textAnchor="middle" dominantBaseline="central" fontSize={12}>
      {pct}%
    </text>
  );
}

export default function RelatoriosPage() {
  // Estados "fonte" (lista completa) — usamos somente no carregamento inicial ou fallback
  const [movs, setMovs] = useState([]);
  // Estados derivados/agrupados (atualizados sem spinner)
  const [pieData, setPieData] = useState([]);                // Atendimentos concluídos por área
  const [avgTimeData, setAvgTimeData] = useState([]);        // Tempo médio por área
  const [statusData, setStatusData] = useState([]);          // Finalizadas x Em aberto
  const [decisoesData, setDecisoesData] = useState([]);      // Decisões (finalizadas)
  const [serie7dData, setSerie7dData] = useState([]);        // Iniciadas x Finalizadas (7d)
  const [pipelineData, setPipelineData] = useState([]);      // Pipeline por situação atual

  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);

  const debounceRef = useRef(null);

  // ------------ cálculo local (fallback) a partir de movs ------------
  const computeFromMovs = useCallback((list) => {
    // 1) Pizza concluídos por área
    const areas = { promotoria: 0, defensoria: 0, saude: 0, educacao: 0, especialistas: 0 };
    list.forEach((m) => {
      if (m.atendimento_promotoria?.check_entrada && m.atendimento_promotoria?.check_saida) areas.promotoria++;
      if (m.atendimento_defensoria?.check_entrada && m.atendimento_defensoria?.check_saida) areas.defensoria++;
      if (m.atendimento_saude?.check_entrada && m.atendimento_saude?.check_saida) areas.saude++;
      if (m.atendimento_educacao?.check_entrada && m.atendimento_educacao?.check_saida) areas.educacao++;
      // CORREÇÃO: usar atendimento_especialistas (key do Resource, não plural duplicado)
      if (m.atendimento_especialistas?.check_entrada && m.atendimento_especialistas?.check_saida) areas.especialistas++;
    });
    const entries = [
      { name: "Promotoria", value: areas.promotoria },
      { name: "Defensoria", value: areas.defensoria },
      { name: "Saúde", value: areas.saude },
      { name: "Educação", value: areas.educacao },
      { name: "Especialistas", value: areas.especialistas },
    ];
    const total = entries.reduce((s, it) => s + it.value, 0) || 1;
    const pie = entries.map((it) => ({ ...it, pct: Math.round((it.value * 100) / total) }));

    // 2) Tempo médio por área (usando ISO8601 do Resource)
    const acc = {
      Promotoria: { tot: 0, n: 0 },
      Defensoria: { tot: 0, n: 0 },
      Saúde: { tot: 0, n: 0 },
      Educação: { tot: 0, n: 0 },
      Especialistas: { tot: 0, n: 0 },
    };
    function add(att, chave) {
      if (!att?.check_entrada || !att?.check_saida) return;
      // created_at é injetado manualmente pelo Resource em cada atendimento
      const createdAt = att.created_at || att.updated_at;
      const updatedAt = att.updated_at;
      if (!createdAt || !updatedAt) return;
      try {
        const a = new Date(createdAt);
        const b = new Date(updatedAt);
        const mins = diffMinutes(a, b);
        if (mins != null && mins > 0) { acc[chave].tot += mins; acc[chave].n += 1; }
      } catch (e) {
        // ignora se parsing falhar
      }
    }
    list.forEach((m) => {
      add(m.atendimento_promotoria, "Promotoria");
      add(m.atendimento_defensoria, "Defensoria");
      add(m.atendimento_saude, "Saúde");
      add(m.atendimento_educacao, "Educação");
      add(m.atendimento_especialistas, "Especialistas");
    });
    const avg = Object.entries(acc).map(([name, v]) => ({
      name,
      minutos: v.n ? Math.round(v.tot / v.n) : 0,
    }));

    // 3) Status (usando campo finalizada do Resource)
    let fin = 0, ab = 0;
    list.forEach((m) => (m.finalizada ? fin++ : ab++));
    const status = [
      { name: "Finalizadas", qtd: fin },
      { name: "Em aberto", qtd: ab },
    ];

    // 4) Decisões (finalizadas) - considera todas as variantes
    let totalFin = 0;
    const mapDec = new Map();
    list.forEach((m) => {
      if (m.finalizada) {
        // Prioriza decisao_flagrante e decisao_mba para Flagrante+MBA
        const decisaoFlagrante = m.decisao_flagrante?.nome;
        const decisaoMba = m.decisao_mba?.nome;
        const decisaoGeral = m.decisao?.nome;

        if (decisaoFlagrante) {
          totalFin++;
          mapDec.set(decisaoFlagrante, (mapDec.get(decisaoFlagrante) || 0) + 1);
        }
        if (decisaoMba) {
          totalFin++;
          mapDec.set(decisaoMba, (mapDec.get(decisaoMba) || 0) + 1);
        }
        // Se não tem flagrante/mba específicos, usa decisao geral
        if (!decisaoFlagrante && !decisaoMba && decisaoGeral) {
          totalFin++;
          mapDec.set(decisaoGeral, (mapDec.get(decisaoGeral) || 0) + 1);
        }
      }
    });
    const arrDec = Array.from(mapDec, ([name, value]) => ({ name, value }));
    arrDec.sort((a, b) => b.value - a.value);
    const TOP = 7;
    let out = arrDec;
    if (arrDec.length > TOP) {
      const top = arrDec.slice(0, TOP);
      const outras = arrDec.slice(TOP).reduce((s, x) => s + x.value, 0);
      top.push({ name: "Outras", value: outras });
      out = top;
    }
    const dec = out.map((it) => ({
      ...it,
      pct: totalFin ? Math.round((it.value * 100) / totalFin) : 0,
    }));

    // 5) Série 7 dias (usando formato ISO8601)
    const base = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      base.push({ key: d.toDateString(), label: fmtDDMM(d), iniciadas: 0, finalizadas: 0 });
    }
    const idxByKey = Object.fromEntries(base.map((b, idx) => [b.key, idx]));
    list.forEach((m) => {
      try {
        const dIni = m.created_at ? new Date(m.created_at) : null;
        if (dIni) {
          const key = new Date(dIni.getFullYear(), dIni.getMonth(), dIni.getDate()).toDateString();
          if (key in idxByKey) base[idxByKey[key]].iniciadas += 1;
        }
        if (m.finalizada) {
          const dFim = m.finalizada_at ? new Date(m.finalizada_at) : null;
          if (dFim) {
            const keyF = new Date(dFim.getFullYear(), dFim.getMonth(), dFim.getDate()).toDateString();
            if (keyF in idxByKey) base[idxByKey[keyF]].finalizadas += 1;
          }
        }
      } catch (e) {
        // ignora se parsing falhar
      }
    });
    const serie = base.map(({ label, iniciadas, finalizadas }) => ({ label, iniciadas, finalizadas }));

    // 6) Pipeline situação atual (usando campo do Resource)
    const mapSit = new Map();
    list.forEach((m) => {
      const nome = m.situacao_atual?.nome || "Sem situação";
      mapSit.set(nome, (mapSit.get(nome) || 0) + 1);
    });
    const pipe = Array.from(mapSit, ([name, qtd]) => ({ name, qtd })).sort((a, b) => b.qtd - a.qtd);

    // aplica nos estados
    setPieData(pie);
    setAvgTimeData(avg);
    setStatusData(status);
    setDecisoesData(dec);
    setSerie7dData(serie);
    setPipelineData(pipe);
    setLastRefreshAt(new Date());
  }, []);

  // ------------ carregamento inicial ------------
  const initialLoad = useCallback(async () => {
    setInitialLoading(true);
    setError(null);
    try {
      // CORREÇÃO: Carrega TODAS as movimentações (sem filtros de data ou status)
      // Passando status=all para ignorar o default (open) do backend
      const resp = await api.get("/movimentacoes?per_page=200&status=all");
      const list = resp.data?.data || [];
      setMovs(list);
      computeFromMovs(list);
      setLastRefreshAt(new Date());
    } catch (e) {
      console.error(e);
      setError("Falha ao carregar dados para os relatórios.");
    } finally {
      setInitialLoading(false);
    }
  }, [computeFromMovs]);

  useEffect(() => {
    initialLoad();
  }, [initialLoad]);

  // ------------ atualização suave via WebSocket (sem spinner) ------------
  const softRefresh = useCallback(async () => {
    try {
      // CORREÇÃO: Busca TODAS as movimentações (sem filtros)
      const resp = await api.get("/movimentacoes?per_page=200&status=all");
      const list = resp.data?.data || [];
      setMovs(list);
      computeFromMovs(list);
      setLastRefreshAt(new Date());
    } catch (e) {
      console.error("[Relatórios] softRefresh falhou:", e);
      // não altera UI (sem spinner)
    }
  }, [computeFromMovs]);

  useEffect(() => {
    const ch = echo.channel("movimentacoes");
    const onSaved = () => {
      // debounce 800ms para agrupar múltiplos eventos
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        softRefresh();
      }, 800);
    };
    ch.listen(".movimentacao.saved", onSaved);
    return () => {
      ch.stopListening(".movimentacao.saved");
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [softRefresh]);

  if (initialLoading) return <LoadingSpinner text="Carregando relatórios..." />;
  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <ProtectedPage permission="relatorios.view" action="visualizar relatórios">
      <div className="content-header">
        <h1 className="content-title">Relatórios (Tempo Real)</h1>
        <p className="content-subtitle">
          Gráficos atualizados automaticamente via WebSocket a cada alteração relevante.
        </p>
        <small className="text-secondary">
          Última atualização: {lastRefreshAt ? lastRefreshAt.toLocaleString() : "—"}
        </small>
      </div>

      <div className="row g-3">
        {/* 1) Pizza: Concluídos por Área (sem donut, com % dentro) */}
        <div className="col-12 col-xl-6">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title mb-3">Atendimentos concluídos por área</h5>
              <div style={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      // pizza (sem innerRadius)
                      outerRadius={110}
                      paddingAngle={2}
                      labelLine={false}
                      label={renderPiePercentLabel}
                    >
                      {pieData.map((_, idx) => (
                        <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      {...TOOLTIP}
                      formatter={(v, n, p) => [`${v} (${p?.payload?.pct ?? 0}%)`, p?.payload?.name]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* 2) Barras: Tempo médio por área (min) */}
        <div className="col-12 col-xl-6">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title mb-3">Tempo médio por área (minutos)</h5>
              <div style={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={avgTimeData}>
                    <CartesianGrid stroke="#1f2d28" />
                    <XAxis dataKey="name" tick={{ fill: "#cfe8dd" }} />
                    <YAxis tick={{ fill: "#cfe8dd" }} />
                    <Tooltip {...TOOLTIP} cursor={false} />
                    <Bar dataKey="minutos">
                      {avgTimeData.map((row, i) => (
                        <Cell
                          key={row.name}
                          fill={AREA_COLORS[row.name] || COLORS[i % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* 3) Barras: Status das movimentações */}
        <div className="col-12 col-xl-6">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title mb-3">Status das movimentações</h5>
              <div style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={statusData}>
                    <CartesianGrid stroke="#1f2d28" />
                    <XAxis dataKey="name" tick={{ fill: "#cfe8dd" }} />
                    <YAxis allowDecimals={false} tick={{ fill: "#cfe8dd" }} />
                    <Tooltip {...TOOLTIP} cursor={false} />
                    <Bar dataKey="qtd">
                      {statusData.map((row, i) => (
                        <Cell
                          key={row.name}
                          fill={STATUS_COLORS[row.name] || COLORS[i % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* 4) Barras horizontais: Decisões mais frequentes (finalizadas) */}
        <div className="col-12 col-xl-6">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title mb-3">Decisões mais frequentes (finalizadas)</h5>
              <div style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={decisoesData} layout="vertical" margin={{ left: 110, right: 16 }}>
                    <CartesianGrid stroke="#1f2d28" />
                    <XAxis type="number" allowDecimals={false} tick={{ fill: "#cfe8dd" }} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fill: "#cfe8dd" }} />
                    <Tooltip
                      {...TOOLTIP}
                      formatter={(v, n, p) => [`${v} (${p?.payload?.pct ?? 0}%)`, "Qtde"]}
                      cursor={false}
                    />
                    <Bar dataKey="value">
                      {decisoesData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* 5) Linhas: Iniciadas × Finalizadas (últimos 7 dias) */}
        <div className="col-12 col-xl-6">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title mb-3">Iniciadas × Finalizadas (últimos 7 dias)</h5>
              <div style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={serie7dData}>
                    <CartesianGrid stroke="#1f2d28" />
                    <XAxis dataKey="label" tick={{ fill: "#cfe8dd" }} />
                    <YAxis allowDecimals={false} tick={{ fill: "#cfe8dd" }} />
                    <Tooltip {...TOOLTIP} />
                    <Legend />
                    <Line type="monotone" dataKey="iniciadas" stroke="#0EA5E9" strokeWidth={2} dot />
                    <Line type="monotone" dataKey="finalizadas" stroke="#22C55E" strokeWidth={2} dot />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* 6) Barras horizontais: Pipeline por Situação Atual */}
        <div className="col-12 col-xl-6">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title mb-3">Pipeline por situação atual</h5>
              <div style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pipelineData} layout="vertical" margin={{ left: 110, right: 16 }}>
                    <CartesianGrid stroke="#1f2d28" />
                    <XAxis type="number" allowDecimals={false} tick={{ fill: "#cfe8dd" }} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fill: "#cfe8dd" }} />
                    <Tooltip {...TOOLTIP} cursor={false} />
                    <Bar dataKey="qtd">
                      {pipelineData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProtectedPage>
  );
}
